import { Link, useLocation, useMatch } from "react-router-dom";
import { useAuthStore, selectUser, canAccessFeature, hasFeatureAccess } from "@safers/auth";

interface TabDef {
  to: string;
  label: string;
  isActive: (pathname: string) => boolean;
  icon: (active: boolean) => React.ReactNode;
}

function iconProps(active: boolean) {
  return {
    width: 22,
    height: 22,
    viewBox: "0 0 24 24",
    fill: "none",
    stroke: "currentColor",
    strokeWidth: active ? 1.9 : 1.6,
    strokeLinecap: "round" as const,
    strokeLinejoin: "round" as const,
  };
}

/**
 * 현장 안에서 쓰는 탭.
 *
 * 모바일에서는 이 하단 바가 **상단 이동 줄을 대신한다.** 같은 이동 수단을 위아래 두 곳에
 * 두면 화면 높이만 먹고, 엄지에서 먼 위쪽이 늘 안 쓰이는 쪽이 된다.
 *
 * 현장목록은 넣지 않는다 — 현장 전환은 대시보드 상단의 전환기가 맡고, 목록 페이지를
 * 따로 거치면 한 단계가 늘 뿐이다.
 */
function siteTabs(id: string): TabDef[] {
  return [
    {
      to: `/site/${id}`,
      label: "대시보드",
      isActive: (p) => p.startsWith("/site/"),
      icon: (active) => (
        <svg {...iconProps(active)}>
          <rect x="2.5" y="4" width="19" height="12.5" rx="2" />
          <path d="M8.5 20.5h7M12 16.5v4" />
        </svg>
      ),
    },
    {
      to: `/cctv-ai/${id}`,
      label: "CCTV",
      isActive: (p) => p.startsWith("/cctv-ai/") || p.startsWith("/cctv-playback/"),
      icon: (active) => (
        <svg {...iconProps(active)}>
          <path d="M3 8.5a2 2 0 0 1 2-2h8a2 2 0 0 1 2 2v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-7Z" />
          <path d="m15 11 5-3v8l-5-3v-2Z" />
        </svg>
      ),
    },
    {
      to: `/safety-report/${id}`,
      label: "보건관리",
      isActive: (p) => p.startsWith("/safety-report/"),
      icon: (active) => (
        <svg {...iconProps(active)}>
          <path d="M20.4 5.9a5 5 0 0 0-7.1 0L12 7.2l-1.3-1.3a5 5 0 1 0-7.1 7.1l1.3 1.3L12 21.4l7.1-7.1 1.3-1.3a5 5 0 0 0 0-7.1Z" />
        </svg>
      ),
    },
  ];
}

const TABS: TabDef[] = [
  {
    to: "/",
    label: "대시보드",
    isActive: (p) => p === "/",
    icon: (active) => (
      <svg {...iconProps(active)}>
        <path d="M3 11l9-7 9 7M5 10v10h14V10" />
      </svg>
    ),
  },
  {
    to: "/health",
    label: "보건관리",
    isActive: (p) => p.startsWith("/health"),
    icon: (active) => (
      <svg {...iconProps(active)}>
        <path d="M3 12h4l2 5 4-10 2 5h6" />
      </svg>
    ),
  },
];

/** 지금 보고 있는 현장 id — 현장 계열 경로 어디에서든 뽑는다. */
function useSiteId(): string | null {
  const site = useMatch("/site/:id");
  const health = useMatch("/safety-report/:id");
  const cctv = useMatch("/cctv-ai/:id");
  const playback = useMatch("/cctv-playback/:id");
  return site?.params.id ?? health?.params.id ?? cctv?.params.id ?? playback?.params.id ?? null;
}

export function BottomTabBar() {
  const { pathname } = useLocation();
  const siteId = useSiteId();
  const user = useAuthStore(selectUser);

  const tabs = siteId ? siteTabs(siteId) : TABS;
  // 권한 없는 기능은 감추지 않고 비활성으로 남긴다 — 없으면 "왜 없지" 가 되고,
  // 눌리게 두면 403 으로 튕긴다(상단 이동 버튼과 같은 규칙).
  const enabled = (to: string) => {
    if (!siteId) return to === "/health" ? hasFeatureAccess(user, "HEALTH") : true;
    if (to.startsWith("/cctv-ai/")) return canAccessFeature(user, siteId, "CCTV");
    if (to.startsWith("/safety-report/")) return canAccessFeature(user, siteId, "HEALTH");
    return true;
  };

  return (
    <nav
      className="flex flex-none items-stretch border-t border-[var(--border-default)] bg-[var(--surface-base)] px-2 pt-2"
      style={{ paddingBottom: "calc(0.625rem + env(safe-area-inset-bottom))" }}
    >
      {tabs.map((tab) => {
        const active = tab.isActive(pathname);
        const on = enabled(tab.to);
        const color = !on
          ? "var(--text-disabled, #C4C7D0)"
          : active
            ? "var(--brand-primary)"
            : "var(--text-tertiary)";
        const body = (
          <>
            {tab.icon(active)}
            <span
              className="text-[0.625rem] leading-[0.875rem]"
              style={{ fontWeight: active ? 600 : 500 }}
            >
              {tab.label}
            </span>
          </>
        );
        const cls = "flex flex-1 flex-col items-center justify-center gap-[0.1875rem]";
        return on ? (
          <Link key={tab.to} to={tab.to} className={cls} style={{ color }}>
            {body}
          </Link>
        ) : (
          <span
            key={tab.to}
            className={`${cls} cursor-not-allowed`}
            style={{ color }}
            title="이 현장의 해당 기능 권한이 없습니다"
          >
            {body}
          </span>
        );
      })}
    </nav>
  );
}
